import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Container from './ui/Container';
import SectionHeading from './ui/SectionHeading';
import Card from './ui/Card';
import { Button } from './ui/Button';
import { useT, useLang } from '../i18n';

const T = {
  es: {
    badge: 'PLATAFORMA MODULAR',
    heading: 'Módulos Cadenia',
    subheading: 'Tres productos que trabajan juntos para transformar su operación logística.',
    discover: 'Descubrir',
    viewProduct: 'Ver producto',
    preview: {
      delivered: 'Entregado',
      origin: 'Origen',
      destination: 'Destino',
    },
    products: {
      visibility: {
        title: 'Cadenia Visibility',
        features: [
          'Visibilidad Unificada 360',
          'Seguimiento y Tiempo Real',
          'ETA dinámico y alertas'
        ],
        description: 'Capa de visibilidad del ciclo de vida del envío con eventos, ETA avanzado y alertas proactivas para gestión operativa.',
      },
      intelligence: {
        title: 'Cadenia Intelligence',
        features: [
          'Supply Chain Analytics',
          'Agentes de IA',
          'Informes de Supply Chain'
        ],
        description: 'Analítica, agente conversacional y optimización para convertir datos en decisiones y acción logística.',
      },
      documents: {
        title: 'Cadenia Documents',
        features: [
          'Documento de Control Digital',
          'Carta de Porte y eCMR',
          'Firma Electrónica Avanzada'
        ],
        description: 'Documentos de transporte digitales con firma electrónica, trazabilidad completa y cumplimiento normativo integrado.',
      },
    },
  },
  en: {
    badge: 'MODULAR PLATFORM',
    heading: 'Cadenia Modules',
    subheading: 'Three products that work together to transform your logistics operation.',
    discover: 'Discover',
    viewProduct: 'View product',
    preview: {
      delivered: 'Delivered',
      origin: 'Origin',
      destination: 'Destination',
    },
    products: {
      visibility: {
        title: 'Cadenia Visibility',
        features: [
          'Unified 360 Visibility',
          'Real-Time Tracking',
          'Dynamic ETA and alerts'
        ],
        description: 'A visibility layer across the shipment lifecycle with events, advanced ETA and proactive alerts for operational management.',
      },
      intelligence: {
        title: 'Cadenia Intelligence',
        features: [
          'Supply Chain Analytics',
          'AI Agents',
          'Supply Chain Reports'
        ],
        description: 'Analytics, a conversational agent and optimization to turn data into decisions and logistics action.',
      },
      documents: {
        title: 'Cadenia Documents',
        features: [
          'Digital Control Document',
          'Consignment Note and eCMR',
          'Advanced Electronic Signature'
        ],
        description: 'Digital transport documents with electronic signature, full traceability and built-in regulatory compliance.',
      },
    },
  },
};

const Products = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isHovering, setIsHovering] = useState(false);
  const t = useT(T);
  const { path } = useLang();

  // Auto-cycle through cards when not hovering
  useEffect(() => {
    if (isHovering) return;
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % 3);
    }, 3000);
    return () => clearInterval(interval);
  }, [isHovering]);

  const handleMouseEnter = useCallback((index) => {
    setIsHovering(true);
    setActiveIndex(index);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsHovering(false);
  }, []);

  const products = [
    {
      id: 'visibility',
      title: t.products.visibility.title,
      features: t.products.visibility.features,
      description: t.products.visibility.description,
      icon: (
        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
        </svg>
      )
    },

    {
      id: 'intelligence',
      title: t.products.intelligence.title,
      features: t.products.intelligence.features,
      description: t.products.intelligence.description,
      icon: (
        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
      )
    },

    {
      id: 'documents',
      title: t.products.documents.title,
      features: t.products.documents.features,
      description: t.products.documents.description,
      icon: (
        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      )
    }
  ];



  return (
    <section id="productos" className="py-32 bg-gradient-to-b from-white to-slate-50">
      <Container>
        <div className="text-center mb-24">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 border border-blue-100 rounded-full text-sm font-medium text-blue-700 mb-8">
            {t.badge}
          </div>
          <h2 className="text-4xl md:text-5xl font-light text-slate-900 mb-6">{t.heading}</h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
            {t.subheading}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {products.map((product, index) => {
            const isActive = activeIndex === index;
            return (
              <Link
                key={product.id}
                to={path(product.id)}
                className="group relative block"
                onMouseEnter={() => handleMouseEnter(index)}
                onMouseLeave={handleMouseLeave}
              >
                {/* Animated glow background */}
                <div className={`absolute -inset-px rounded-2xl transition-all duration-700 ease-in-out ${isActive ? 'opacity-100 bg-gradient-to-br from-blue-500/20 via-indigo-500/10 to-blue-500/20 blur-sm scale-[1.02]' : 'opacity-0 scale-100'}`}></div>

                <div className={`relative bg-white rounded-2xl border h-full flex flex-col transition-all duration-500 ease-in-out overflow-hidden ${isActive ? 'shadow-xl shadow-blue-500/10 border-blue-200 -translate-y-1' : 'shadow-lg border-slate-100 translate-y-0'}`}>
                  {/* Thumbnail preview */}
                  <div className={`relative h-44 bg-slate-50 border-b border-slate-100 overflow-hidden transition-all duration-500 ${isActive ? 'bg-gradient-to-br from-slate-50 to-blue-50' : ''}`}>
                    {product.id === 'visibility' && (
                      <div className="absolute inset-0 p-5 flex items-center justify-center">
                        <div className="w-full bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                          {/* Shipment header */}
                          <div className="px-3 py-2 border-b border-slate-100 flex items-center justify-between">
                            <div className="flex items-center gap-1.5">
                              <svg className="w-3 h-3 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                              <span className="text-[10px] font-mono font-medium text-slate-700">10234795</span>
                            </div>
                            <span className="text-[8px] bg-green-50 text-green-600 border border-green-200 rounded px-1.5 py-0.5 font-medium">{t.preview.delivered}</span>
                          </div>
                          {/* Origin → Destination */}
                          <div className="px-3 py-2 flex items-center gap-2">
                            <div className="flex-1">
                              <div className="text-[8px] text-slate-400">{t.preview.origin}</div>
                              <div className="text-[10px] text-slate-700 font-medium">Madrid</div>
                            </div>
                            <div className="flex flex-col items-center px-2">
                              <div className="w-8 h-px bg-slate-200 relative">
                                <div className="absolute inset-0 bg-green-400 rounded"></div>
                              </div>
                              <svg className="w-3 h-3 text-green-500 -mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M14 5l7 7m0 0l-7 7m7-7H3" /></svg>
                            </div>
                            <div className="flex-1 text-right">
                              <div className="text-[8px] text-slate-400">{t.preview.destination}</div>
                              <div className="text-[10px] text-slate-700 font-medium">Bilbao</div>
                            </div>
                          </div>
                          {/* Mini timeline */}
                          <div className="px-3 py-2 border-t border-slate-100 flex items-center gap-1.5">
                            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                            <div className="h-px bg-green-300 flex-1"></div>
                            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                            <div className="h-px bg-green-300 flex-1"></div>
                            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                            <div className="h-px bg-green-300 flex-1"></div>
                            <div className="w-2.5 h-2.5 bg-green-500 rounded-full flex items-center justify-center">
                              <svg className="w-1.5 h-1.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={4} d="M5 13l4 4L19 7" /></svg>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    {product.id === 'intelligence' && (
                      <div className="absolute inset-0 p-5 flex flex-col">
                        {/* KPI row */}
                        <div className="flex gap-2 mb-3">
                          <div className="flex-1 bg-white rounded-lg p-2 border border-slate-100 text-center">
                            <div className="text-sm font-light text-indigo-600">96%</div>
                          </div>
                          <div className="flex-1 bg-white rounded-lg p-2 border border-slate-100 text-center">
                            <div className="text-sm font-light text-indigo-600">1.4d</div>
                          </div>
                          <div className="flex-1 bg-white rounded-lg p-2 border border-slate-100 text-center">
                            <div className="text-sm font-light text-indigo-600">4.3K</div>
                          </div>
                        </div>
                        {/* AI chat — more recognizable */}
                        <div className="flex-1 bg-white rounded-lg border border-indigo-100 p-2.5 flex flex-col gap-2">
                          {/* User message */}
                          <div className="flex justify-end">
                            <div className="bg-indigo-500 rounded-xl rounded-tr-sm px-2.5 py-1.5">
                              <div className="w-20 h-1.5 bg-indigo-300 rounded"></div>
                            </div>
                          </div>
                          {/* AI response with icon */}
                          <div className="flex items-start gap-1.5">
                            <div className="w-5 h-5 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0">
                              <svg className="w-3 h-3 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" /></svg>
                            </div>
                            <div className="bg-slate-50 rounded-xl rounded-tl-sm p-2 flex-1">
                              <div className="space-y-1">
                                <div className="h-1.5 bg-slate-200 rounded w-full"></div>
                                <div className="h-1.5 bg-indigo-100 rounded w-4/5"></div>
                                <div className="h-1.5 bg-slate-200 rounded w-3/5"></div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    {product.id === 'documents' && (
                      <div className="absolute inset-0 p-5 flex items-center justify-center">
                        <div className="w-40 bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                          {/* Doc header */}
                          <div className="px-3 py-2 border-b border-slate-100 flex items-center justify-between">
                            <div className="text-[10px] font-medium text-slate-700">eCMR</div>
                            <div className="w-5 h-5 bg-slate-100 rounded grid grid-cols-2 gap-px p-0.5">
                              <div className="bg-slate-300 rounded-sm"></div>
                              <div className="bg-slate-300 rounded-sm"></div>
                              <div className="bg-slate-300 rounded-sm"></div>
                              <div className="bg-slate-300 rounded-sm"></div>
                            </div>
                          </div>
                          {/* Content lines */}
                          <div className="px-3 py-2 space-y-1.5">
                            <div className="h-1.5 bg-slate-200 rounded w-full"></div>
                            <div className="h-1.5 bg-slate-100 rounded w-4/5"></div>
                            <div className="h-1.5 bg-slate-200 rounded w-full"></div>
                          </div>
                          {/* Signature area */}
                          <div className="px-3 py-2 border-t border-slate-100">
                            <div className="flex items-center gap-2">
                              <div className="flex gap-1">
                                <div className="w-4 h-4 rounded bg-emerald-100 flex items-center justify-center">
                                  <svg className="w-2.5 h-2.5 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                                </div>
                                <div className="w-4 h-4 rounded bg-emerald-100 flex items-center justify-center">
                                  <svg className="w-2.5 h-2.5 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                                </div>
                                <div className="w-4 h-4 rounded bg-slate-100"></div>
                              </div>
                              <svg className="w-14 h-5 text-emerald-400 ml-auto" viewBox="0 0 60 20"><path d="M2 14 Q10 3 18 12 T34 6 T50 14 T58 8" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="p-8 pt-6 flex flex-col flex-grow">
                    <h3 className="text-2xl font-light text-slate-900 mb-4">{product.title}</h3>

                    <ul className="space-y-3 mb-6">
                      {product.features.map((feature, i) => (
                        <li key={i} className="flex items-start">
                          <div className={`w-1.5 h-1.5 rounded-full mt-1.5 mr-3 flex-shrink-0 transition-colors duration-500 ${isActive ? 'bg-blue-500' : 'bg-slate-300'}`}></div>
                          <span className="text-sm text-slate-700 leading-relaxed">{feature}</span>
                        </li>
                      ))}
                    </ul>

                    <p className="text-sm text-slate-500 mb-8 leading-relaxed flex-grow">{product.description}</p>

                    <div>
                      <span
                        className={`inline-flex items-center gap-2 text-sm font-medium transition-all duration-300 ${isActive ? 'text-blue-600' : 'text-slate-500 group-hover:text-blue-600'}`}
                      >
                        {t.discover} {product.title.split(' ')[1]}
                        <svg className={`w-4 h-4 transition-transform duration-300 ${isActive ? 'translate-x-1' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" /></svg>
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>

        {/* Progress dots */}
        <div className="flex items-center justify-center gap-2 mt-12">
          {products.map((_, index) => (
            <button
              key={index}
              onClick={() => { setActiveIndex(index); setIsHovering(true); setTimeout(() => setIsHovering(false), 5000); }}
              className={`transition-all duration-500 rounded-full ${activeIndex === index ? 'w-8 h-2 bg-blue-500' : 'w-2 h-2 bg-slate-300 hover:bg-slate-400'}`}
              aria-label={`${t.viewProduct} ${index + 1}`}
            />
          ))}
        </div>
      </Container>
    </section>
  );
};

export default Products;